import { inject, Service, signal } from '@angular/core';
import { StorageService } from '../modules/checklist/services/storage.service';
import { Checklist } from '../modules/checklist/types/ChecklistTypes';
import { withDerivedProgress } from '../modules/checklist/utils/checklist-progress';

@Service()
export class ChecklistStoreService {
  private readonly storageService = inject(StorageService);

  private readonly _checklists = signal<Checklist[]>([]);
  private readonly _selectedChecklistId = signal<string | null>(null);
  private readonly _selectedChecklist = signal<Checklist | null>(null);
  private readonly checklistWriteGeneration = new Map<string, number>();
  private readonly isolatedPreviewIds = new Set<string>();

  readonly checklists = this._checklists.asReadonly();
  readonly selectedChecklist = this._selectedChecklist.asReadonly();
  readonly selectedChecklistId = this._selectedChecklistId.asReadonly();

  markIsolatedPreview(id: string): void {
    this.isolatedPreviewIds.add(id);
  }

  unmarkIsolatedPreview(id: string): void {
    this.isolatedPreviewIds.delete(id);
  }

  isIsolatedPreview(id: string): boolean {
    return this.isolatedPreviewIds.has(id);
  }

  find(id: string): Checklist | undefined {
    return this._checklists().find(c => c.id === id);
  }

  latest(checklistId: string, fallback?: Checklist): Checklist | null {
    const selected = this._selectedChecklist();
    if (selected?.id === checklistId) {
      return selected;
    }

    return this.find(checklistId) ?? fallback ?? null;
  }

  writeGeneration(checklistId: string): number | undefined {
    return this.checklistWriteGeneration.get(checklistId);
  }

  setAll(
    checklists: Checklist[],
    { persist = true }: { persist?: boolean } = {}
  ): void {
    this._checklists.set(checklists);
    // The persisted list is shared by every tab of the app, so a tab holding an isolated preview must not overwrite it.
    if (persist && !checklists.some(c => this.isolatedPreviewIds.has(c.id))) {
      void this.storageService.saveChecklists(checklists);
    }
  }

  update(checklist: Checklist): void {
    const updatedChecklist = withDerivedProgress(checklist);
    this.bumpWriteGeneration(updatedChecklist.id);
    this.setAll(
      this._checklists().map(c =>
        c.id === updatedChecklist.id ? updatedChecklist : c
      )
    );
    if (updatedChecklist.id === this._selectedChecklist()?.id) {
      this._selectedChecklist.set(updatedChecklist);
    }
  }

  merge(loadedChecklist: Checklist, options: { persist?: boolean } = {}): void {
    const checklist = withDerivedProgress(loadedChecklist);
    const currentChecklists = this._checklists();
    const exists = currentChecklists.some(c => c.id === checklist.id);
    const updatedChecklists = exists
      ? currentChecklists.map(c => (c.id === checklist.id ? checklist : c))
      : [...currentChecklists, checklist];

    this.setAll(updatedChecklists, { persist: options.persist !== false });

    if (this._selectedChecklistId() === checklist.id) {
      this._selectedChecklist.set(checklist);
    }
  }

  replace(checklist: Checklist): void {
    this.setAll(
      this._checklists().map(c => (c.id === checklist.id ? checklist : c))
    );
    this.bumpWriteGeneration(checklist.id);
    if (this._selectedChecklistId() === checklist.id) {
      this._selectedChecklist.set(checklist);
    }
  }

  restore(
    checklistId: string,
    checklists: Checklist[],
    selected: Checklist | null
  ): void {
    this.setAll(checklists);
    this.bumpWriteGeneration(checklistId);
    if (this._selectedChecklistId() === checklistId) {
      this._selectedChecklist.set(selected);
    }
  }

  // Selection is matched on the selected object's id, not `_selectedChecklistId`, which can lag
  // the object (on mobile portrait the route effect's `select` may not have run yet).
  replaceWithoutBump(checklist: Checklist): void {
    this.setAll(
      this._checklists().map(c => (c.id === checklist.id ? checklist : c))
    );
    if (this._selectedChecklist()?.id === checklist.id) {
      this._selectedChecklist.set(checklist);
    }
  }

  select(id: string | null): void {
    this._selectedChecklistId.set(id);
    const checklist = id === null ? null : this.find(id);
    if (id === null || checklist) {
      this._selectedChecklist.set(checklist ?? null);
    }
  }

  private bumpWriteGeneration(checklistId: string): void {
    this.checklistWriteGeneration.set(
      checklistId,
      (this.checklistWriteGeneration.get(checklistId) ?? 0) + 1
    );
  }
}
